import { SLOT_CARDINALITY } from '#pplx/frontend/composer-sdk/internal/slots/slots.js';
import { useMemo } from 'react';
/** Resolves the mounted plugins into per-slot placements, memoized on the plugin list. */
export function useSlotPlacements(plugins) {
    return useMemo(() => buildSlotPlacements(plugins), [plugins]);
}
/** @returns Whether every chin contribution allows the shared region to animate. */
export function shouldAnimateChin(placements) {
    return placements.chin?.every(({ animate }) => animate !== false) ?? true;
}
/**
 * Group every mounted plugin's placements by the slot they target, rejecting
 * duplicate plugin ids and over-filled `one` slots.
 *
 * @throws if two plugins share an `id`, or more than one component targets a
 * `one`-cardinality slot — surfacing misconfiguration at mount, not mid-render.
 */
function buildSlotPlacements(plugins) {
    const seenIds = new Set();
    const placements = {};
    // Collect each plugin's placements into their target slots (rejecting id clashes).
    for (const plugin of plugins) {
        if (seenIds.has(plugin.id)) {
            throw new Error(`Composer plugins: duplicate plugin id "${plugin.id}".`);
        }
        seenIds.add(plugin.id);
        for (const placement of plugin.components) {
            (placements[placement.slot] ??= []).push({
                pluginId: plugin.id,
                component: placement.component,
                props: plugin.props,
                visible: placement.slot === 'chin' ? placement.visible?.(plugin.props ?? {}) : undefined,
                animate: placement.slot === 'chin' ? (placement.animate?.(plugin.props ?? {}) ?? true) : undefined,
            });
        }
    }
    // Enforce cardinality: `one` slots may hold at most a single component.
    for (const slot of Object.keys(placements)) {
        const slotPlacements = placements[slot] ?? [];
        if (SLOT_CARDINALITY[slot] === 'one' && slotPlacements.length > 1) {
            const owners = slotPlacements.map((placement) => placement.pluginId).join(', ');
            throw new Error(`Composer slot "${slot}" accepts one component but got: ${owners}.`);
        }
    }
    return placements;
}
//# sourceMappingURL=useSlotPlacements.js.map